import {
  Modal,
  Box,
  Typography,
  TextField,
  MenuItem,
  Grid,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Button } from "@mui/material";
import { useState, useEffect } from "react";
import api from '../config/api';
import { useTranslation } from 'react-i18next';

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 0,
  p: 4,
  border: "none",
  outline: "none",
  maxHeight: "90vh",
  overflowY: "auto",
};

const allRoles = [
  { value: "super-admin", label: "Super Admin" },
  { value: "admin-grafton", label: "Admin Grafton" },
  { value: "admin", label: "Admin" },
  { value: "user", label: "Usuario" },
];

const UserModal = ({
  openModal,
  handleClose,
  type = "add",
  selectedUser = null,
  authToken,
  allOrganizations = [],
  showAlert,
  setCounter,
  organizationId,
  currentUserRole, // Necesitas pasar el rol del usuario actual como prop
}) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    rol: "user",
  });
  const [loading, setLoading] = useState(false);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState({});

  const { t } = useTranslation();

  // Función para obtener los roles disponibles según el rol del usuario actual
  const getAvailableRoles = () => {
    switch (currentUserRole) {
      case "super-admin":
        // El super-admin puede crear todos los tipos de usuarios
        return allRoles;
      case "admin-grafton":
        // El admin-grafton solo puede crear admin y user
        return allRoles.filter(role =>
          ["admin", "user"].includes(role.value)
        );
      case "admin":
        // El admin solo puede crear user
        return allRoles.filter(role => role.value === "user");
      default:
        // Por defecto, solo usuario
        return allRoles.filter(role => role.value === "user");
    }
  };

  const availableRoles = getAvailableRoles();

  useEffect(() => {
    if (type === "edit" && selectedUser) {
      setForm({
        name: selectedUser.name || "",
        email: selectedUser.email || "",
        password: "",
        rol: selectedUser.rol || "user",
        erpRole: selectedUser.erpRole || "",
      });

      // Si estamos editando, cargar las organizaciones y proyectos actuales del usuario
      if (selectedUser.organizations) {
        setSelectedOrganizations(selectedUser.organizations || []);
      }

      // Construye el mapa orgUid -> [projectUids] igual que UserOrganizationModal
      const initialProjects = {};
      allOrganizations.forEach((org) => {
        if (selectedUser.organizations?.includes(org.uid)) {
          const matchedProjects = org.projects
            ?.filter((p) => selectedUser.projects?.includes(p.uid))
            .map((p) => p.uid);

          initialProjects[org.uid] = matchedProjects || [];
        }
      });
      setSelectedProjects(initialProjects);
    } else {
      // Configurar el rol por defecto al primer rol disponible
      const defaultRole = availableRoles[0]?.value || "user";
      setForm({
        name: "",
        email: "",
        password: "",
        rol: defaultRole,
        erpRole: ""
      });
      setSelectedOrganizations([]);
      setSelectedProjects({});
    }
  }, [type, selectedUser, availableRoles]);

  useEffect(() => {
    const isSingleOrgRole = ["user", "admin"].includes(form?.rol);
    if (!isSingleOrgRole) return;

    setSelectedOrganizations((prev) => {
      const only = prev?.[0];
      return only ? [only] : [];
    });

    setSelectedProjects((prev) => {
      const keys = Object.keys(prev || {});
      const only = keys[0];
      return only ? { [only]: prev[only] } : {};
    });
  }, [form.rol]);


  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleOrgChange = (event) => {
    const incoming = event.target.value;

    // Roles que SOLO pueden quedar con 1 organización
    const isSingleOrgRole = ["user", "admin"].includes(form?.rol);

    const orgUids = isSingleOrgRole
      ? (incoming.length ? [incoming[incoming.length - 1]] : [])
      : incoming;

    setSelectedOrganizations(orgUids);

    setSelectedProjects((prev) => {
      if (isSingleOrgRole) {
        const onlyOrg = orgUids[0];
        if (!onlyOrg) return {};

        const org = allOrganizations.find((o) => o.uid === onlyOrg);

        const matchedProjects =
          org?.projects
            ?.filter((p) => selectedUser?.projects?.includes(p.uid))
            .map((p) => p.uid) || [];

        return { [onlyOrg]: prev[onlyOrg] ?? matchedProjects };
      }

      const updated = { ...prev };

      orgUids.forEach((uid) => {
        if (updated[uid] === undefined) {
          const org = allOrganizations.find((o) => o.uid === uid);
          const matchedProjects =
            org?.projects
              ?.filter((p) => selectedUser?.projects?.includes(p.uid))
              .map((p) => p.uid) || [];
          updated[uid] = matchedProjects;
        }
      });

      Object.keys(updated).forEach((uid) => {
        if (!orgUids.includes(uid)) delete updated[uid];
      });

      return updated;
    });
  };

  const handleProjectChange = (orgId, value) => {
    setSelectedProjects((prev) => ({
      ...prev,
      [orgId]: value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (type === "add") {
        const res = await api.post(
          `/users/register?organizationId=${selectedOrganizations[0] || "null"
          }`,
          form,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        const uid = res.data.response.data.uid;

        // Rol ERP en creación: register no persiste el campo, se asigna aparte
        if (form.erpRole && currentUserRole === "super-admin") {
          await api.put(
            `/users/${uid}`,
            { erpRole: form.erpRole },
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
        }

        if (form.rol !== "super-admin") {
          await api.put(
            `/organizations/assignOrganizations/${uid}`,
            { organizations: selectedOrganizations },
            { headers: { Authorization: `Bearer ${authToken}` } }
          );

          const allProjects = Array.from(
            new Set(
              selectedOrganizations.flatMap((orgId) => selectedProjects[orgId] || [])
            )
          );

          await api.put(
            `/projects/assignProjectsDirectly/${uid}`,
            { projects: allProjects },
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
        }

        showAlert(t('users.userCreateSuccess'), "success");
      } else if (type === "edit") {
        // Actualizar la información básica del usuario
        await api.put(
          `/users/${selectedUser.uid}`,
          {
            name: form.name,
            rol: form.rol,
            // Rol ERP v2 (admin/bp/back_office/kam/compras): solo super-admin
            ...(currentUserRole === "super-admin"
              ? { erpRole: form.erpRole || null }
              : {}),
          },
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        // Actualizar organizaciones si el usuario no es super-admin
        if (form.rol !== "super-admin") {
          await api.put(
            `/organizations/assignOrganizations/${selectedUser.uid}`,
            { organizations: selectedOrganizations },
            { headers: { Authorization: `Bearer ${authToken}` } }
          );

          // Actualizar proyectos para cada organización
          const allProjects = Array.from(
            new Set(
              selectedOrganizations.flatMap((orgId) => selectedProjects[orgId] || [])
            )
          );

          await api.put(
            `/projects/assignProjectsDirectly/${selectedUser.uid}`,
            { projects: allProjects },
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
        }

        showAlert("Usuario actualizado correctamente", "success");
      } else if (type === "delete") {
        await api.delete(
          `/users/${selectedUser.uid}`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        showAlert(t('users.userEditSuccess'), "success");
      }

      handleClose();
      setCounter?.((prev) => prev + 1);
    } catch (err) {
      console.error(err);
      showAlert(t('generalApiError'), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={openModal} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" sx={{ textAlign: "center" }}>
          {type === "add"
            ? t('users.userCreate')
            : type === "edit"
              ? t('users.userEdit')
              : t('users.userDelete')}
        </Typography>

        {type === "delete" ? (
          <Typography align="center" sx={{ fontSize: "1.1rem", mt: 3 }}>
            {t('users.userDeleteConfirm')}{" "}
            <strong>{selectedUser?.name}</strong>? {t('noWayBack')}
          </Typography>
        ) : (
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t('name')}
                  name="name"
                  fullWidth
                  value={form.name}
                  onChange={handleChange}
                />
              </Grid>

              {type === "add" && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label={t('email')}
                      name="email"
                      fullWidth
                      value={form.email}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t('password')}
                      name="password"
                      type="password"
                      fullWidth
                      value={form.password}
                      onChange={handleChange}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <TextField
                  select
                  label={t('role')}
                  name="rol"
                  fullWidth
                  value={form.rol}
                  onChange={handleChange}
                >
                  {availableRoles.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {currentUserRole === "super-admin" && (
                <Grid item xs={12}>
                  <TextField
                    select
                    label={t('users.erpRole')}
                    helperText={t('users.erpRoleHelper')}
                    name="erpRole"
                    fullWidth
                    value={form.erpRole || ""}
                    onChange={handleChange}
                  >
                    <MenuItem value="">{t('users.erpRoleNone')}</MenuItem>
                    <MenuItem value="admin">{t('users.erpRoles.admin')}</MenuItem>
                    <MenuItem value="bp">{t('users.erpRoles.bp')}</MenuItem>
                    <MenuItem value="back_office">{t('users.erpRoles.back_office')}</MenuItem>
                    <MenuItem value="kam">{t('users.erpRoles.kam')}</MenuItem>
                    <MenuItem value="compras">{t('users.erpRoles.compras')}</MenuItem>
                  </TextField>
                </Grid>
              )}

              {(type === "add" || type === "edit") && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label={t('organizations.title')}
                      fullWidth
                      select
                      SelectProps={{ multiple: true }}
                      value={selectedOrganizations}
                      onChange={handleOrgChange}
                      sx={{ mb: 3 }}
                      disabled={form.rol === "super-admin"}
                    >
                      {allOrganizations.map((org) => (
                        <MenuItem
                          key={org.uid}
                          value={org.uid}
                          sx={{
                            "&.Mui-selected": {
                              backgroundColor: "#e0e0e0",
                              fontWeight: 600,
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: "#d5d5d5",
                            },
                          }}
                        >
                          {org.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {selectedOrganizations.map((orgUid) => {
                    const org = allOrganizations.find((o) => o.uid === orgUid);
                    if (!org) return null;

                    return (
                      <Grid item xs={12} key={orgUid}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {t('users.projectsOf')} {org.name}
                        </Typography>
                        <TextField
                          label={t('projects.title')}
                          fullWidth
                          select
                          SelectProps={{ multiple: true }}
                          value={selectedProjects[orgUid] || []}
                          onChange={(e) =>
                            handleProjectChange(orgUid, e.target.value)
                          }
                          disabled={form.rol === "super-admin"}
                        >
                          {(org.projects || []).map((project) => (
                            <MenuItem
                              key={project.uid}
                              value={project.uid}
                              sx={{
                                "&.Mui-selected": {
                                  backgroundColor: "#e0e0e0",
                                  fontWeight: 600,
                                },
                                "&.Mui-selected:hover": {
                                  backgroundColor: "#d5d5d5",
                                },
                              }}
                            >
                              {project.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    );
                  })}
                </>
              )}
            </Grid>
          </Box>
        )}

        <Grid container spacing={2} justifyContent="center" sx={{ mt: 4 }}>
          <Grid item>
            <Button variant="outlined" onClick={handleClose} disabled={loading}>
              {t('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <LoadingButton
              variant={type === "delete" ? "outlined" : "contained"}
              color={type === "delete" ? "error" : "primary"}
              onClick={handleSubmit}
              loading={loading}
            >
              {type === "delete" ? t('delete') : t('save')}
            </LoadingButton>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default UserModal;